// Catalog of report metrics available per channel, mapping each one to an
// existing backend data endpoint and a normalized widget-data shape that both
// the on-screen widgets and the PDF generator understand.
//
// Each entry:
//   { id, channel, type, label, unit?, request(ctx) -> { path, params }, map(data) -> widgetData }
// where ctx = { userId, startDate, endDate } and widgetData is one of:
//   kpi:       { value }
//   breakdown: { items: [{ label, value }] }
//   table:     { columns: [{ key, label, align?, unit? }], rows: [...] }

export const CHANNELS = {
  ga: { key: "ga", label: "Google Analytics", short: "GA" },
  gads: { key: "gads", label: "Google Ads", short: "Ads" },
  meta: { key: "meta", label: "Meta Ads", short: "Meta" },
  linkedin: { key: "linkedin", label: "LinkedIn Ads", short: "LinkedIn" },
};

export const CHANNEL_ORDER = ["ga", "gads", "meta", "linkedin"];

export function formatValue(value, unit, opts = {}) {
  if (value === null || value === undefined || value === "") return "\u2014";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);

  let s;
  if (opts.compact && Math.abs(n) >= 1000) {
    s = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 }).format(n);
  } else {
    s = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(n);
  }

  const u = unit ? String(unit) : "";
  if (u === "%") return `${s}%`;
  if (u === "$" || u.toLowerCase() === "usd") return `$${s}`;
  return u ? `${s} ${u}` : s;
}

const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

// Standard scalar envelopes return { value } (GA/Meta/LinkedIn) or { value } via
// a `field`/`value` pair (Ads). Most just need Number(data.value).
const kpiValue = (data) => ({ value: toNum(data && data.value) });

// Time-series endpoints return { points: [{ x, y }] }; wrap into a named series.
const seriesMap = (name) => (data) => ({
  series: [{ name, points: Array.isArray(data && data.points) ? data.points : [] }],
});

const baseParams = (ctx) => ({
  userId: ctx.userId,
  startDate: ctx.startDate,
  endDate: ctx.endDate,
});

export const CATALOG = [
  // ---------------- Google Analytics ----------------
  { id: "ga.sessions", channel: "ga", type: "kpi", label: "Sessions",
    request: (ctx) => ({ path: "/analytics/sessions", params: baseParams(ctx) }), map: kpiValue },
  { id: "ga.total_users", channel: "ga", type: "kpi", label: "Total Users",
    request: (ctx) => ({ path: "/analytics/total-users", params: baseParams(ctx) }), map: kpiValue },
  { id: "ga.new_users", channel: "ga", type: "kpi", label: "New Users",
    request: (ctx) => ({ path: "/analytics/new-users", params: baseParams(ctx) }), map: kpiValue },
  { id: "ga.engagement_rate", channel: "ga", type: "kpi", label: "Engagement Rate", unit: "%",
    request: (ctx) => ({ path: "/analytics/engagement-rate", params: baseParams(ctx) }),
    map: (data) => ({ value: toNum(data && data.value) * 100 }) },
  { id: "ga.total_revenue", channel: "ga", type: "kpi", label: "Total Revenue", unit: "$",
    request: (ctx) => ({ path: "/analytics/total-revenue", params: baseParams(ctx) }), map: kpiValue },
  { id: "ga.trend_sessions", channel: "ga", type: "timeseries", label: "Sessions (Trend)",
    request: (ctx) => ({ path: "/analytics/timeseries", params: { ...baseParams(ctx), metric: "sessions" } }),
    map: seriesMap("Sessions") },
  { id: "ga.trend_users", channel: "ga", type: "timeseries", label: "Total Users (Trend)",
    request: (ctx) => ({ path: "/analytics/timeseries", params: { ...baseParams(ctx), metric: "totalUsers" } }),
    map: seriesMap("Total Users") },
  { id: "ga.top_regions", channel: "ga", type: "breakdown", label: "Top Regions by Sessions",
    request: (ctx) => ({ path: "/analytics/top-regions", params: { ...baseParams(ctx), limit: 6 } }),
    map: (data) => ({
      items: (data.rows || []).map((r) => ({ label: r[data.dimension], value: toNum(r[data.metric]) })),
    }) },
  { id: "ga.top_pages", channel: "ga", type: "table", label: "Top Pages",
    request: (ctx) => ({ path: "/analytics/top-pages", params: { ...baseParams(ctx), limit: 8 } }),
    map: (data) => ({
      columns: [
        { key: "page", label: "Page", align: "left" },
        { key: "views", label: "Views", align: "right" },
      ],
      rows: (data.rows || []).map((r) => ({ page: r[data.dimension], views: toNum(r[data.metric]) })),
    }) },

  // ---------------- Google Ads ----------------
  { id: "gads.clicks", channel: "gads", type: "kpi", label: "Clicks",
    request: (ctx) => ({ path: "/ads/clicks", params: baseParams(ctx) }), map: kpiValue },
  { id: "gads.impressions", channel: "gads", type: "kpi", label: "Impressions",
    request: (ctx) => ({ path: "/ads/impressions", params: baseParams(ctx) }), map: kpiValue },
  { id: "gads.ctr", channel: "gads", type: "kpi", label: "CTR", unit: "%",
    request: (ctx) => ({ path: "/ads/ctr", params: baseParams(ctx) }),
    map: (data) => ({ value: toNum(data && data.value) * 100 }) },
  { id: "gads.spend", channel: "gads", type: "kpi", label: "Spend", unit: "$",
    request: (ctx) => ({ path: "/ads/spend", params: baseParams(ctx) }),
    map: (data) => ({ value: toNum(data && data.costMicros) / 1e6 }) },
  { id: "gads.conversions", channel: "gads", type: "kpi", label: "Conversions",
    request: (ctx) => ({ path: "/ads/conversions", params: baseParams(ctx) }),
    map: (data) => ({ value: toNum(data && (data.value != null ? data.value : data.total)) }) },
  { id: "gads.trend_clicks", channel: "gads", type: "timeseries", label: "Clicks (Trend)",
    request: (ctx) => ({ path: "/ads/timeseries", params: { ...baseParams(ctx), metric: "clicks" } }),
    map: seriesMap("Clicks") },
  { id: "gads.trend_spend", channel: "gads", type: "timeseries", label: "Spend (Trend)", unit: "$",
    request: (ctx) => ({ path: "/ads/timeseries", params: { ...baseParams(ctx), metric: "cost" } }),
    map: seriesMap("Spend") },
  { id: "gads.spend_by_campaign", channel: "gads", type: "breakdown", label: "Spend by Campaign", unit: "$",
    request: (ctx) => ({ path: "/ads/spend-by-campaign", params: { ...baseParams(ctx), limit: 6 } }),
    map: (data) => ({
      items: (data.rows || []).map((r) => ({ label: r.campaignName, value: toNum(r.costMicros) / 1e6 })),
    }) },
  { id: "gads.top_keywords", channel: "gads", type: "table", label: "Top Keywords by Clicks",
    request: (ctx) => ({ path: "/ads/top-keywords-by-clicks", params: { ...baseParams(ctx), limit: 8 } }),
    map: (data) => ({
      columns: [
        { key: "keyword", label: "Keyword", align: "left" },
        { key: "clicks", label: "Clicks", align: "right" },
      ],
      rows: (data.rows || []).map((r) => ({ keyword: r.keyword, clicks: toNum(r.clicks) })),
    }) },

  // ---------------- Meta Ads ----------------
  { id: "meta.impressions", channel: "meta", type: "kpi", label: "Impressions",
    request: (ctx) => ({ path: "/meta/impressions", params: baseParams(ctx) }), map: kpiValue },
  { id: "meta.clicks", channel: "meta", type: "kpi", label: "Clicks",
    request: (ctx) => ({ path: "/meta/clicks", params: baseParams(ctx) }), map: kpiValue },
  { id: "meta.reach", channel: "meta", type: "kpi", label: "Reach",
    request: (ctx) => ({ path: "/meta/reach", params: baseParams(ctx) }), map: kpiValue },
  { id: "meta.spend", channel: "meta", type: "kpi", label: "Spend", unit: "$",
    request: (ctx) => ({ path: "/meta/spend", params: baseParams(ctx) }), map: kpiValue },
  { id: "meta.ctr", channel: "meta", type: "kpi", label: "CTR", unit: "%",
    request: (ctx) => ({ path: "/meta/ctr", params: baseParams(ctx) }), map: kpiValue },
  { id: "meta.cpc", channel: "meta", type: "kpi", label: "CPC", unit: "$",
    request: (ctx) => ({ path: "/meta/cpc", params: baseParams(ctx) }), map: kpiValue },
  { id: "meta.trend_impressions", channel: "meta", type: "timeseries", label: "Impressions (Trend)",
    request: (ctx) => ({ path: "/meta/timeseries", params: { ...baseParams(ctx), metric: "impressions" } }),
    map: seriesMap("Impressions") },
  { id: "meta.trend_spend", channel: "meta", type: "timeseries", label: "Spend (Trend)", unit: "$",
    request: (ctx) => ({ path: "/meta/timeseries", params: { ...baseParams(ctx), metric: "spend" } }),
    map: seriesMap("Spend") },
  { id: "meta.cost_per_conv_by_campaign", channel: "meta", type: "breakdown",
    label: "Cost / Conversion by Campaign", unit: "$",
    request: (ctx) => ({ path: "/meta/cost_per_conversion_by_campaign", params: { ...baseParams(ctx), limit: 6 } }),
    map: (data) => ({
      items: (data.rows || []).map((r) => ({ label: r.campaign_name, value: toNum(r.cost_per_conversion) })),
    }) },

  // ---------------- LinkedIn Ads ----------------
  { id: "linkedin.impressions", channel: "linkedin", type: "kpi", label: "Impressions",
    request: (ctx) => ({ path: "/linkedin/impressions", params: baseParams(ctx) }), map: kpiValue },
  { id: "linkedin.clicks", channel: "linkedin", type: "kpi", label: "Clicks",
    request: (ctx) => ({ path: "/linkedin/clicks", params: baseParams(ctx) }), map: kpiValue },
  { id: "linkedin.spend", channel: "linkedin", type: "kpi", label: "Spend", unit: "$",
    request: (ctx) => ({ path: "/linkedin/spend", params: baseParams(ctx) }), map: kpiValue },
  { id: "linkedin.ctr", channel: "linkedin", type: "kpi", label: "CTR", unit: "%",
    request: (ctx) => ({ path: "/linkedin/ctr", params: baseParams(ctx) }), map: kpiValue },
  { id: "linkedin.conversions", channel: "linkedin", type: "kpi", label: "Conversions",
    request: (ctx) => ({ path: "/linkedin/conversions", params: baseParams(ctx) }), map: kpiValue },
  { id: "linkedin.trend_impressions", channel: "linkedin", type: "timeseries", label: "Impressions (Trend)",
    request: (ctx) => ({ path: "/linkedin/timeseries", params: { ...baseParams(ctx), metric: "impressions" } }),
    map: seriesMap("Impressions") },
  { id: "linkedin.trend_clicks", channel: "linkedin", type: "timeseries", label: "Clicks (Trend)",
    request: (ctx) => ({ path: "/linkedin/timeseries", params: { ...baseParams(ctx), metric: "clicks" } }),
    map: seriesMap("Clicks") },
  { id: "linkedin.cost_per_conversion", channel: "linkedin", type: "table",
    label: "Cost / Conversion by Campaign",
    request: (ctx) => ({ path: "/linkedin/cost_per_conversion", params: { ...baseParams(ctx), limit: 8 } }),
    map: (data) => ({
      columns: [
        { key: "campaign", label: "Campaign", align: "left" },
        { key: "spend", label: "Spend", align: "right", unit: "$" },
        { key: "conversions", label: "Conv.", align: "right" },
        { key: "cpc", label: "Cost / Conv.", align: "right", unit: "$" },
      ],
      rows: (data.campaigns || []).map((c) => ({
        campaign: c.campaign_name,
        spend: toNum(c.spend),
        conversions: toNum(c.conversions),
        cpc: toNum(c.cost_per_conversion),
      })),
    }) },
];

export const CATALOG_BY_ID = CATALOG.reduce((acc, entry) => {
  acc[entry.id] = entry;
  return acc;
}, {});

export function catalogForChannels(connections) {
  return CHANNEL_ORDER.filter((key) => connections && connections[key]).map((key) => ({
    channel: CHANNELS[key],
    metrics: CATALOG.filter((m) => m.channel === key),
  }));
}
